// import config from 'config';

import { get, post } from "../../../api/axios-client";
import {
  Filter,
  UpdateStatusWisePosition,
  UpdateWorkOrder,
  UpdateWorkOrderTech,
  WorkOrder,
  WorkOrderHistory,
  WorkOrderCollection,
  WorkOrderSegmentDetail,
  WorkOrderStatusOrder,
  workOrderTechnicianOrder,
  WorkOrderPriorityOrder,
  WorkOrderArchive,
  UpdateWorkOrderPriority,
} from "../models/workOrderTypes";
import {
  isConcurrencyError,
  ConcurrencyError,
} from "../../common/Utils/concurrencyError";

const controllerUrl = "api/WorkOrders/";

export const WorkOrderQueueAPI = {
  updateWorkOrder: async (param: WorkOrder): Promise<WorkOrder | null> => {
    try {
      const { data } = await post(`${controllerUrl}UpdateWorkOrder`, param);
      return data;
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      throw error;
    }
  },
  getWorkOrder: async (
    workOrderIdentity: number,
  ): Promise<WorkOrder | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetWorkOrderByIdentity?workOrderIdentity=${workOrderIdentity}`,
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getWorkOrderHistory: async (
    workOrderIdentity: number,
  ): Promise<WorkOrderHistory[] | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetWorkOrderHistoryByIdentity?workOrderIdentity=${workOrderIdentity}`,
      );
      return data;
    } catch (error) {
      throw error;
    }
  },

  getWorkOrdersBystatus: async (
    param: Filter,
  ): Promise<WorkOrderCollection | null> => {
    const { data } = await post(`${controllerUrl}GetFilteredWorkOrders`, param);
    return data.content;
  },
  updateWorkOrdersPosition: async (listOfWo: UpdateStatusWisePosition) => {
    const { data } = await post(
      `${controllerUrl}UpdateWorkOrdersPositions`,
      listOfWo,
    );
    return data;
  },
  updateWorkOrderStatus: async (
    param: UpdateWorkOrder,
  ): Promise<boolean | null> => {
    try {
      const { data } = await post(
        `${controllerUrl}UpdateWorkOrderStatus`,
        {
          ...param,
          workOrderStatusIdentity:
            param.workOrderStatusIdentity ?? param.WorkOrderStatusIdentity,
        },
      );
      const content =
        typeof data === "boolean"
          ? data
          : (data?.content ?? data?.Content ?? false);
      if (content === false) {
        return false;
      }
      return Boolean(content);
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      return false;
    }
  },

  updateWorkOrdersstatusPositions: async (
    updatedPositions: UpdateStatusWisePosition,
  ): Promise<string | null> => {
    const url = `${controllerUrl}UpdateWorkOrdersstatusPositions`;
    const { data } = await post(url, updatedPositions);
    return data;
  },

  UpdateWorkOrderTechnician: async (
    updatedPositions: UpdateWorkOrderTech,
  ): Promise<string | null> => {
    try {
      const url = `${controllerUrl}UpdateWorkOrderTechnician`;
      const { data } = await post(url, updatedPositions);
      return data;
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      throw error;
    }
  },
  updateWorkOrdersTechPositions: async (
    updatedPositions: UpdateStatusWisePosition,
  ): Promise<string | null> => {
    const url = `${controllerUrl}UpdateWorkOrdersTechPositions`;
    const { data } = await post(url, updatedPositions);
    return data;
  },
  createWorkOrder: async (param: WorkOrder): Promise<WorkOrder | null> => {
    try {
      const { data } = await post(`${controllerUrl}CreateWorkOrder`, param);
      return data;
    } catch (error) {
      throw error;
    }
  },

  /**
   * Creates a smart work order for technician calendar
   * This creates a work order and automatically schedules it on the selected technician's calendar
   */
  createSmartWOForTechCalendar: async (param: any): Promise<any> => {
    try {
      const { data } = await post(`${controllerUrl}CreateSmartWOForTechCalendar`, param);
      const result = data?.content || data;

      // Check for API-level errors (HTTP 200 but business logic error)
      if (result?.isSuccess === false) {
        throw new Error(result.message || result.errorMessage || "Failed to create work order");
      }

      return result;
    } catch (error) {
      throw error;
    }
  },

  archiveWorkOrder: async (param: WorkOrderArchive): Promise<string | null> => {
    try {
      const { data } = await post(`${controllerUrl}ArchiveWorkOrder`, param);
      return data;
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      throw error;
    }
  },

  getSegmentDetails: async (
    segmentIdentity: number,
  ): Promise<WorkOrderSegmentDetail | null> => {
    try {
      const { data } = await get(
        `${controllerUrl}GetSegmentDetailsByIdentity?segmentIdentity=${segmentIdentity}`,
      );

      return data;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },

  updateSegmentEstimatedHours: async (
    segmentId: number,
    flatRateLaborHrs: number,
    modBy: string,
    modDate: string,
    rowVersion?: string,
  ): Promise<boolean> => {
    try {
      const { data } = await post(
        `${controllerUrl}UpdateSegmentEstimatedHours`,
        {
          segmentId,
          flatRateLaborHrs,
          modBy,
          modDate,
          rowVersion,
        },
      );
      return data.content || data.Content || data.Data || data.data || data;
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      console.error("Error updating segment estimated hours:", error);
      throw error;
    }
  },

  getSegmentImageAttachment: async (currentSegmentDetails): Promise<any> => {
    const segAttachedImages = await Promise.all(
      currentSegmentDetails.images.map((image) =>
        fetch(
          `${controllerUrl}GetSegmentAttachedImage?attachmentIdentity=${image.attachmentIdentity}`,
        )
          .then((response) => response.blob())
          .then((blob) => URL.createObjectURL(blob)),
      ),
    );
    return segAttachedImages;
  },
  getSegmentImageByIdentity: async (
    attachmentIdentity: number,
  ): Promise<any> => {
    try {
      const response = await get(`${controllerUrl}GetSegmentAttachedImage`, {
        params: { attachmentIdentity }, // Send as query parameter
        responseType: "blob", // Ensure the response is a binary blob
      });

      const imageUrl = URL.createObjectURL(response.data); // Create a URL for the image blob
      return imageUrl;
    } catch (error) {
      return error;
    }
  },

  saveWorkOrderStatusOrder: async (
    payload: WorkOrderStatusOrder[],
  ): Promise<WorkOrderSegmentDetail | null> => {
    try {
      const { data } = await post(
        `${controllerUrl}SaveWorkOrderStatusOrder`,
        payload,
      );
      return data;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },

  saveWorkOrderTechOrder: async (
    payload: workOrderTechnicianOrder[],
  ): Promise<WorkOrderSegmentDetail | null> => {
    try {
      const { data } = await post(
        `${controllerUrl}SaveWorkOrderTechOrder`,
        payload,
      );
      return data;
    } catch (err) {
      //console.log(err);
      return null;
    }
  },

  updateWorkOrderPriority: async (
    param: UpdateWorkOrderPriority,
  ): Promise<boolean | null> => {
    try {
      const { data } = await post(`${controllerUrl}UpdateWorkOrderPriority`, {
        invoiceDocumentIdentity: param.invoiceDocumentIdentity,
        woPriorityIdentity: param.woPriorityIdentity,
        modifiedDate: param.modifiedDate,
        rowVersion: param.rowVersion,
      });
      return data;
    } catch (error) {
      if (isConcurrencyError(error)) {
        throw new ConcurrencyError(
          error.response?.data?.message ||
            "This Work Order has been updated by another user. Please close and reopen this Work Order to get the latest changes.",
        );
      }
      return false;
    }
  },

  updateWorkOrdersPriorityPositions: async (
    updatedPositions: UpdateStatusWisePosition,
  ): Promise<string | null> => {
    const { data } = await post(
      `${controllerUrl}UpdateWorkOrdersPriorityPositions`,
      updatedPositions,
    );
    return data;
  },

  saveWorkOrderPriorityOrder: async (
    payload: WorkOrderPriorityOrder[],
  ): Promise<WorkOrderSegmentDetail | null> => {
    try {
      const { data } = await post(
        `${controllerUrl}SaveWorkOrderPriorityOrder`,
        payload,
      );
      return data;
    } catch (err) {
      return null;
    }
  },

  IsWorkOrderItFinalized: async (
    invoiceDocumentIdentity: number,
  ): Promise<boolean> => {
    try {
      const { data } = await get(
        `${controllerUrl}IsWorkOrderFinalized?invoiceDocIdentity=${invoiceDocumentIdentity}`,
      );
      return Boolean(data);
    } catch (error) {
      throw error;
    }
  },

  // Logistics API
  getWorkOrdersForLogistics: async (
    filters: Filter,
  ): Promise<WorkOrder[] | null> => {
    try {
      const { data } = await post(
        `${controllerUrl}GetWorkOrdersForLogistics`,
        filters,
      );
      return data;
    } catch (error) {
      //console.log(error);
      return null;
    }
  },
  // moved to InvoiceAPI
};
export default WorkOrderQueueAPI;
